import React, { useState, useEffect, useMemo } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Dropdown } from "../../../components/Dropdown";
import { Button } from "../../../components/Button";
import indiaData from "../../../data/stateData.json"; // states + districts JSON
import { apiPost, apiPut } from "../../../services/apiServices";
import { endPoints } from "../../../configs/apiUrls";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import Switch from "../../../components/Switch";

const AddUser = () => {
    const { id } = useParams<{ id?: string }>();
    const location = useLocation();
    const navigate = useNavigate();

    const {
        usersList,
        filterData,
    } = useSelector((state: RootState) => state.users);

    const isEditMode = Boolean(id);

    const [initialValues, setInitialValues] = useState({
        userType: "",
        name: "",
        mobileNumber: "",
        state: "",
        city: "",
        area: "",
        experience: "",
        certificates: "",
        awards: "",
        password: "",
        isApproved: false,
    });

    useEffect(() => {
        setInitialValues({
            ...initialValues,
            userType: filterData.userType
        });
    }, [filterData.userType]);

    useEffect(() => {
        if (isEditMode && location.state?.row) {
            setInitialValues(location.state.row); // 👈 prefill from previous screen
        }
    }, [isEditMode, location.state]);

    // State dropdown options
    const stateOptions = useMemo(
        () => indiaData.map((s) => ({ label: s.state, value: s.state })),
        []
    );

    // Flatten city list
    const cityOptions = useMemo(
        () =>
            indiaData.flatMap((s) =>
                s.districts.map((d) => ({
                    label: d,
                    value: d,
                    state: s.state,
                }))
            ),
        []
    );

    const validationSchema = Yup.object().shape({
        userType: Yup.string().required("User type is required"),
        name: Yup.string().min(3).required("Name is required"),
        mobileNumber: Yup.string()
            .matches(/^\d{10}$/, "Mobile number must be 10 digits")
            .required("Mobile number is required"),
        state: Yup.string().required("State is required"),
        city: Yup.string().required("City is required"),
        experience: Yup.number()
            .nullable()
            .when("userType", {
                is: (val: string) => val === "agent" || val === "valuer",
                then: (schema) =>
                    schema
                        .min(0, "Experience cannot be negative")
                        .max(50, "Experience seems too high")
                        .required("Experience is required"),
                otherwise: (schema) => schema.notRequired(),
            }),
        certificates: Yup.string().nullable(),
        awards: Yup.string().nullable(),
        password: Yup.string().test(
            "password-validation",
            "Password is required and must be minimum 8 characters long with uppercase, lowercase, number and special character",
            function (value) {
                const { isEditMode } = this.options.context || {};
                if (isEditMode) return true; // skip validation on edit
                if (!value) return false;
                return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])/.test(value) && value.length >= 8;
            }
        ),
    });

    const handleSubmit = async (
        values: typeof initialValues,
        { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
    ) => {
        setSubmitting(true);
        try {
            const res = isEditMode ? await apiPut(`${endPoints.UPDATE_USER}/${id}`, values) : await apiPost(endPoints.CREATE_USER, values);
            if (res?.success) {
                toast.success("User saved successfully!");
                navigate(-1);
            } else {
                toast.error(res.data.message || `Failed to ${isEditMode ? "update" : "create"} user`);
            }
            setSubmitting(false);
        } catch (error: any) {
            toast.error(error.data.message || `Failed to ${isEditMode ? "update" : "create"} user`);
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto p-6 bg-cardBackground rounded shadow">
            <h2 className="text-2xl font-semibold mb-6 text-headingText">
                {isEditMode ? "Edit User" : "Add New User"}
            </h2>

            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue, isSubmitting }) => {
                    const filteredCityOptions = cityOptions.filter(
                        (c) => c.state === values.state
                    );

                    return (
                        <Form className="grid grid-cols-1 md:grid-cols-3 gap-10">
                            {/* Name */}
                            <div>
                                <label className="block text-sm font-medium text-subTitleText mb-1">
                                    Name <span className="text-errorText">*</span>
                                </label>
                                <Field
                                    type="text"
                                    name="name"
                                    placeholder="Enter full name"
                                    className="w-full border border-borderColor rounded px-3 py-2"
                                />
                                <ErrorMessage
                                    name="name"
                                    component="div"
                                    className="mt-1 text-errorText text-sm"
                                />
                            </div>

                            {/* Mobile Number */}
                            <div>
                                <label className="block text-sm font-medium text-subTitleText mb-1">
                                    Mobile Number <span className="text-errorText">*</span>
                                </label>
                                <Field
                                    type="tel"
                                    name="mobileNumber"
                                    placeholder="Enter 10 digit mobile number"
                                    className="w-full border border-borderColor rounded px-3 py-2"
                                />
                                <ErrorMessage
                                    name="mobileNumber"
                                    component="div"
                                    className="mt-1 text-errorText text-sm"
                                />
                            </div>

                            {/* Password */}
                            <div>
                                <label className="block text-sm font-medium text-subTitleText mb-1">
                                    Password {!isEditMode && <span className="text-errorText">*</span>}
                                </label>
                                <Field
                                    type="password"
                                    name="password"
                                    placeholder="Enter password"
                                    className="w-full border border-borderColor rounded px-3 py-2"
                                    autoComplete={isEditMode ? "new-password" : "off"}
                                />
                                <ErrorMessage
                                    name="password"
                                    component="div"
                                    className="mt-1 text-errorText text-sm"
                                />
                            </div>

                            {/* State */}
                            <div>
                                <label className="block text-sm font-medium text-subTitleText mb-1">
                                    State <span className="text-errorText">*</span>
                                </label>
                                <Dropdown
                                    options={stateOptions}
                                    value={values.state || ""}
                                    onChange={(val: string) => {
                                        setFieldValue("state", val);
                                        setFieldValue("city", "");
                                    }}
                                    placeholder="Select state"
                                    className="w-full"
                                />
                                <ErrorMessage
                                    name="state"
                                    component="div"
                                    className="mt-1 text-errorText text-sm"
                                />
                            </div>

                            {/* City */}
                            <div>
                                <label className="block text-sm font-medium text-subTitleText mb-1">
                                    City <span className="text-errorText">*</span>
                                </label>
                                <Dropdown
                                    options={filteredCityOptions}
                                    value={values.city || ""}
                                    onChange={(val: string) => {
                                        setFieldValue("city", val);
                                    }}
                                    placeholder="Select city"
                                    disabled={!values.state}
                                    className="w-full"
                                />
                                <ErrorMessage
                                    name="city"
                                    component="div"
                                    className="mt-1 text-errorText text-sm"
                                />
                            </div>

                            {/* Area */}
                            {filterData?.userType !== "user" && (
                                <div>
                                    <label className="block text-sm font-medium text-subTitleText mb-1">
                                        Area
                                    </label>
                                    <Field
                                        type="text"
                                        name="area"
                                        placeholder="Enter area name"
                                        className="w-full border border-borderColor rounded px-3 py-2"
                                    />
                                </div>
                            )}

                            {/* Experience */}
                            {(values.userType === "agent" || values.userType === "valuer") && (
                                <div>
                                    <label className="block text-sm font-medium text-subTitleText mb-1">
                                        Experience (years) <span className="text-errorText">*</span>
                                    </label>
                                    <Field
                                        type="number"
                                        name="experience"
                                        min={0}
                                        placeholder="Enter years of experience"
                                        className="w-full border border-borderColor rounded px-3 py-2"
                                    />
                                    <ErrorMessage
                                        name="experience"
                                        component="div"
                                        className="mt-1 text-errorText text-sm"
                                    />
                                </div>
                            )}

                            {/* Certificates + Awards */}
                            {values.userType === "valuer" && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-subTitleText mb-1">
                                            Certificates
                                        </label>
                                        <Field
                                            as="textarea"
                                            name="certificates"
                                            placeholder="Enter certificates"
                                            className="w-full border border-borderColor rounded px-3 py-2"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-subTitleText mb-1">
                                            Awards
                                        </label>
                                        <Field
                                            as="textarea"
                                            name="awards"
                                            placeholder="Enter awards"
                                            className="w-full border border-borderColor rounded px-3 py-2"
                                        />
                                    </div>
                                </>
                            )}

                            {values.userType === "valuer" && (
                                <div className="flex items-center gap-4">
                                    <label className="block text-sm font-medium text-subTitleText">
                                        Approve
                                    </label>
                                    <Switch
                                        checked={values.isApproved}
                                        onCheckedChange={(val: boolean) => setFieldValue("isApproved", val)}
                                    />
                                </div>
                            )}

                            {/* Submit */}
                            <div className="md:col-span-3 pt-4">
                                <Button loading={isSubmitting} variant="primary" type="submit">
                                    {isEditMode ? "Update" : "Create"}
                                </Button>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};

export default AddUser;